import { useCallback, useEffect, useRef, useState } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { NodeProps } from "@xyflow/react"

import { Database, Plus, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"
import { NodeEditDialog } from "./node-edit-dialog"

export function VariableNode({ id, data, selected }: NodeProps) {
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)

  const handleModeChange = useCallback(
    (value: string) => {
      updateNodeData(id, { mode: value })
    },
    [id, updateNodeData]
  )

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateNodeData(id, { variableName: e.target.value })
    },
    [id, updateNodeData]
  )

  const handleDefaultChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateNodeData(id, { defaultValue: e.target.value })
    },
    [id, updateNodeData]
  )

  const handleListChange = useCallback(
    (items: string[]) => {
      updateNodeData(id, { defaultValue: JSON.stringify(items) })
    },
    [id, updateNodeData]
  )

  const handleTypeChange = useCallback(
    (value: string) => {
      updateNodeData(id, { variableType: value })
    },
    [id, updateNodeData]
  )

  const handleShowOutputChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showOutput: checked })
    },
    [id, updateNodeData]
  )

  const handleShowInActivityHistoryChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showInActivityHistory: checked })
    },
    [id, updateNodeData]
  )

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  const mode = (data.mode as string) || "set"
  const variableName = (data.variableName as string) || ""
  const variableType = (data.variableType as string) || "string"
  const defaultValue = (data.defaultValue as string) || ""
  const hasOutput = isCompleted && !!output
  const renderEditor = (isModal = false) => (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <Label className="text-xs">Mode</Label>
        <Select value={mode} onValueChange={handleModeChange}>
          <SelectTrigger className="nodrag h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="set">Set Variable</SelectItem>
            <SelectItem value="get">Get Variable</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-1.5">
        <Label className="text-xs">Variable Name</Label>
        <Input
          className="nodrag h-7 text-xs"
          placeholder="my_variable"
          value={variableName}
          onChange={handleNameChange}
        />
      </div>
      <div className="grid gap-1.5">
        <Label className="text-xs">Type</Label>
        <Select value={variableType} onValueChange={handleTypeChange}>
          <SelectTrigger className="nodrag h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="string">String</SelectItem>
            <SelectItem value="number">Number</SelectItem>
            <SelectItem value="boolean">Boolean</SelectItem>
            <SelectItem value="list">List</SelectItem>
            <SelectItem value="json">JSON</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {mode === "set" && variableType === "list" && (
        <ListValueEditor defaultValue={defaultValue} onChange={handleListChange} />
      )}
      {mode === "set" && variableType !== "list" && (
        <div className="grid gap-1.5">
          <Label className="text-xs">Default Value (optional)</Label>
          <Input
            className="nodrag h-7 text-xs"
            value={defaultValue}
            placeholder={
              {
                json: '{"key": "value"}',
                boolean: "true or false",
                number: "0",
                string: "Leave empty to use input",
              }[variableType] || "Leave empty to use input"
            }
            onChange={handleDefaultChange}
          />
        </div>
      )}
      <div className="grid gap-2 border-t pt-2">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={!!data.showOutput}
            className="nodrag"
            id={`${id}-showOutput${isModal ? "-modal" : ""}`}
            onCheckedChange={handleShowOutputChange}
          />
          <Label
            className="cursor-pointer text-xs font-normal"
            htmlFor={`${id}-showOutput${isModal ? "-modal" : ""}`}
          >
            Show Output
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={data.showInActivityHistory !== false}
            className="nodrag"
            id={`${id}-showInActivityHistory${isModal ? "-modal" : ""}`}
            onCheckedChange={handleShowInActivityHistoryChange}
          />
          <Label
            className="cursor-pointer text-xs font-normal"
            htmlFor={`${id}-showInActivityHistory${isModal ? "-modal" : ""}`}
          >
            Show in Activity History
          </Label>
        </div>
      </div>
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[200px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[2])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium">
            <Database className="size-4" />
            Variable
            <NodeEditDialog
              description="Edit this variable node in a larger workspace."
              title="Variable settings"
            >
              {renderEditor(true)}
            </NodeEditDialog>
          </CardTitle>
          {selected && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-3">
          {renderEditor()}
          <Handle position={Position.Left} type="target" />
          <Handle position={Position.Right} type="source" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}

interface ListValueEditorProps {
  defaultValue: string
  onChange: (items: string[]) => void
}

interface ListRow {
  id: string
  value: string
}

let listRowIdCounter = 0
function nextListRowId(): string {
  listRowIdCounter += 1
  return `list-row-${listRowIdCounter}`
}

// Parse the persisted defaultValue (JSON array, or newline/comma separated text)
// into plain string values. Mirrors the legacy fallbacks so existing flows keep
// their items.
function parseListValues(defaultValue: string): string[] {
  if (!defaultValue) return [""]
  try {
    const parsed = JSON.parse(defaultValue)
    if (Array.isArray(parsed)) {
      return parsed.length > 0 ? parsed.map((v: unknown) => String(v)) : [""]
    }
    return [defaultValue]
  } catch {
    // Try splitting by newlines or commas
    if (defaultValue.includes("\n")) {
      const split = defaultValue.split("\n").filter((x) => x.trim())
      return split.length > 0 ? split : [""]
    }
    if (defaultValue.includes(",")) {
      const split = defaultValue
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
      return split.length > 0 ? split : [""]
    }
    return [defaultValue]
  }
}

function toRows(values: string[]): ListRow[] {
  return values.map((value) => ({ id: nextListRowId(), value }))
}

// Exported for focused unit tests; the workflow editor uses it internally via
// VariableNode.renderEditor.
export function ListValueEditor({ defaultValue, onChange }: ListValueEditorProps) {
  // Hold the list in local state with stable per-row ids so React keys never
  // collide with array indices. Typing/removal therefore preserves focus and
  // caret position instead of re-deriving fresh rows from the JSON each render.
  const [rows, setRows] = useState<ListRow[]>(() => toRows(parseListValues(defaultValue)))

  // Track the JSON we last emitted so an incoming defaultValue update that
  // merely echoes our own edit doesn't blow away local rows (and focus). Only
  // an external change (e.g. another editor, undo/redo, node reset) resyncs.
  const lastEmitted = useRef<string>(JSON.stringify(rows.map((row) => row.value)))

  useEffect(() => {
    if (defaultValue === lastEmitted.current) return
    setRows(toRows(parseListValues(defaultValue)))
    lastEmitted.current = defaultValue
  }, [defaultValue])

  // Commit the next rows: update local state and persist as a JSON array,
  // keeping lastEmitted in sync so the resync effect can distinguish our own
  // write from an external one. Side effects live here (not inside a setRows
  // updater) so the updater stays pure under StrictMode double-invocation.
  const commit = useCallback(
    (nextRows: ListRow[]) => {
      setRows(nextRows)
      const values = nextRows.map((row) => row.value)
      lastEmitted.current = JSON.stringify(values)
      onChange(values)
    },
    [onChange]
  )

  const handleItemChange = (id: string, value: string) => {
    commit(rows.map((row) => (row.id === id ? { ...row, value } : row)))
  }

  const handleAddItem = () => {
    commit([...rows, { id: nextListRowId(), value: "" }])
  }

  const handleRemoveItem = (id: string) => {
    commit(
      rows.length <= 1 ? [{ id: nextListRowId(), value: "" }] : rows.filter((row) => row.id !== id)
    )
  }

  return (
    <div className="grid gap-1.5">
      <Label className="text-xs">List Items</Label>
      <div className="grid gap-1">
        {rows.map((row, index) => (
          <div key={row.id} className="flex items-center gap-1">
            <Input
              className="nodrag h-6 flex-1 text-xs"
              placeholder={`Item ${index + 1}`}
              value={row.value}
              onChange={(e) => handleItemChange(row.id, e.target.value)}
            />
            <Button
              className="nodrag size-6 shrink-0 p-0"
              size="icon"
              variant="ghost"
              onClick={() => handleRemoveItem(row.id)}
            >
              <X className="text-muted-foreground size-3" />
            </Button>
          </div>
        ))}
      </div>
      <Button
        className="nodrag h-6 w-full text-xs"
        size="sm"
        variant="outline"
        onClick={handleAddItem}
      >
        <Plus className="mr-1 size-3" />
        Add item
      </Button>
    </div>
  )
}
